/* eslint-disable @typescript-eslint/no-explicit-any */
import { parseLocalDateTime } from "@/common/utils";
import { LocalDateTime } from "@js-joda/core";
import { DeviceDetails } from "../device-details";
import { Entity } from "../entity";
import {
  RawAmount,
  RawCurrencyRef,
  RawDenomination,
  RawExtraData,
  RawTransaction,
  RawTransactions,
  RawEarlyValueNotification,
  RawTransactionServicePoint,
} from "./transaction.types";

export enum EEarlyValueNotificationType {
  CERT = "CERT",
  NO_CERT = "NO_CERT",
  FILE = "FILE",
  NOT = "NOT",
  NO_NOT = "NO_NOT",
}

export class EarlyValueNotification {
  readonly id: string;
  readonly date: LocalDateTime | undefined;
  readonly source: string;
  readonly type: EEarlyValueNotificationType;
  constructor(data: RawEarlyValueNotification) {
    this.id = data.id ? data.id : "";
    this.date = parseLocalDateTime(data.date);
    this.source = data.source ? data.source : "";
    this.type = EEarlyValueNotificationType[data.type as keyof typeof EEarlyValueNotificationType];
  }
}

export class Amount {
  readonly total: number;
  readonly barCodes: string[];
  readonly currency: string;
  readonly numItems: number;
  readonly id: string;
  readonly origin: string;
  readonly categories: string[];
  accreditation: EarlyValueNotification | undefined;
  notification: EarlyValueNotification | undefined;
  constructor(data: RawAmount) {
    this.id = data.id;
    this.total = data.total;
    this.currency = data.currency;
    this.numItems = data.numItems;
    this.origin = data.origin;
    this.categories = data.categories || [];

    const barCodes: string[] = [];
    if (data.barCodes) data.barCodes.filter((rawdata) => Boolean(rawdata)).forEach((rawData) => barCodes.push(rawData));
    this.barCodes = barCodes;
    this.accreditation = data.accreditation ? new EarlyValueNotification(data.accreditation) : undefined;
    this.notification = data.notification ? new EarlyValueNotification(data.notification) : undefined;
  }
}

export class Denomination {
  readonly date: string;
  readonly type: string;
  readonly total: number;
  readonly value: number;
  readonly barCode: string;
  readonly boxName: string;
  readonly subType: string;
  readonly category: string;
  readonly categoryStatus: string;
  readonly codeLine: string;
  readonly currency: string;
  readonly effectiveCurrency: string;
  readonly numItems: number;
  readonly cardNumber: string;
  readonly description: string;
  readonly collectionId: string;
  readonly isValid: boolean;
  readonly origin: string;
  constructor(data: RawDenomination) {
    this.date = data.date;
    this.type = data.type;
    this.total = data.total;
    this.value = data.value;
    this.barCode = data.barCode;
    this.boxName = data.boxName;
    this.subType = data.subType;
    this.category = data.category;
    this.categoryStatus = data.categoryStatus || "";
    this.codeLine = data.codeLine;
    this.currency = data.currency;
    this.effectiveCurrency = data.effectiveCurrency || data.currency;
    this.numItems = data.numItems;
    this.cardNumber = data.cardNumber;
    this.description = data.description;
    this.collectionId = data.collectionId;
    this.isValid = data.isValid ?? false;
    this.origin = data.origin || "";
  }
}

export class ExtraData {
  readonly name: string;
  readonly value: string;
  constructor(data: RawExtraData) {
    this.name = data.name;
    this.value = data.value;
  }
}

export class CurrencyRef {
  readonly currency: string;
  readonly exponent: number;
  constructor(data: RawCurrencyRef) {
    this.currency = data.currency;
    this.exponent = data.exponent;
  }
}

export class TransactionServicePoint {
  readonly id: string;
  readonly isEarlyValue: boolean;
  readonly name: string;
  readonly centerCode: string;
  readonly centerName: string;
  constructor(data?: RawTransactionServicePoint) {
    this.id = data?.id || "";
    this.isEarlyValue = data?.isEarlyValue || false;
    this.name = data?.name || "";
    this.centerCode = data?.centerCode || "";
    this.centerName = data?.centerName || "";
  }
}

export enum ETransactionType {
  CASHIN = "CASHIN",
  CASHOUT = "CASHOUT",
  SHIPIN = "SHIPIN",
  SHIPOUT = "SHIPOUT",
  V_SHIPOUT = "V_SHIPOUT",
  MOVEIN = "MOVEIN",
  MOVEOUT = "MOVEOUT",
  BBALANCE = "BBALANCE",
  EBALANCE = "EBALANCE",
}

export enum ETransactionOrigin {
  CASH = "CASH",
  DECLARED = "DECLARED",
  COINACCEPTOR = "COINACCEPTOR",
  NOTEACCEPTOR = "NOTEACCEPTOR",
  COINRECYCLER = "COINRECYCLER",
  NOTERECYCLER = "NOTERECYCLER",
  COINSTACKER = "COINSTACKER",
  NOTESTACKER = "NOTESTACKER",
  COINDISPENSER = "COINDISPENSER",
  NOTEDISPENSER = "NOTEDISPENSER",
  NOTESREJECTED = "NOTESREJECTED",
  TRANSPORTBOX = "TRANSPORTBOX",
  CARDREADER = "CARDREADER",
  CHECKREADER = "CHECKREADER",
  DROPSAFE = "DROPSAFE",
}

export class Transaction {
  readonly accountingDate: LocalDateTime | undefined;
  readonly actualId: string;
  readonly amounts: Amount[];
  readonly centerCode: string;
  readonly centerId: string;
  readonly channel: string;
  readonly collectionId: string;
  readonly countryId: string;
  readonly currencies: CurrencyRef[];
  readonly delegation: string;
  readonly denominations: Denomination[];
  readonly deviceDetails: DeviceDetails;
  readonly deviceId: string;
  readonly entity: Entity;
  readonly entityId: string;
  readonly extraDatas: ExtraData[];
  readonly id: string;
  readonly hasAccreditation: boolean;
  readonly hasNotification: boolean;
  readonly origin: string;
  readonly receiptNumber: string;
  readonly servicePoint: TransactionServicePoint;
  readonly servicePointCode: string;
  readonly servicePointId: string;
  readonly strapSealCode: string;
  readonly systemEntryDate: LocalDateTime | undefined;
  readonly teller: string;
  readonly tellerLogin: string;
  readonly tellerName: string;
  readonly timeZone: string;
  readonly transactionDate: LocalDateTime | undefined;
  readonly transactionInfo: string;
  readonly transactionRef: string;
  readonly transactionSingleOriginId: string;
  readonly transactionType: ETransactionType;
  constructor(data: RawTransaction) {
    this.id = data.id;
    this.accountingDate = this.parseDateValue(data.accountingDate);
    this.actualId = data.actualId;
    this.centerCode = data?.centerCode || "";
    this.centerId = data?.centerId || "";
    this.channel = data.channel;
    this.collectionId = data.collectionId;
    this.countryId = data.countryId;
    this.delegation = data.delegation;
    this.deviceDetails = new DeviceDetails(data.deviceDetails);
    this.deviceId = data.deviceId;
    this.entity = new Entity(data.entity);
    this.entityId = data.entityId;
    const originValue = data.origin || data.origins?.[0] || "";
    this.origin = ETransactionOrigin[originValue as keyof typeof ETransactionOrigin] || originValue;
    this.receiptNumber = data.receiptNumber;
    this.servicePoint = new TransactionServicePoint(data.servicePoint);
    this.servicePointCode = data.servicePointCode;
    this.servicePointId = data.servicePointId;
    this.strapSealCode = data.strapSealCode;
    this.systemEntryDate = this.parseDateValue(data.systemEntryDate);
    this.teller = data.teller;
    this.tellerLogin = data.tellerLogin;
    this.tellerName = data.tellerName;
    this.timeZone = data.timeZone;
    this.transactionDate = this.parseDateValue(data.transactionDate);
    this.transactionInfo = data.transactionInfo;
    this.transactionRef = data.transactionRef;
    this.transactionSingleOriginId = data.transactionSingleOriginId || "";
    this.transactionType = ETransactionType[data.transactionType as keyof typeof ETransactionType];
    this.hasAccreditation = !!data.hasAccreditation;
    this.hasNotification = !!data.hasNotification;

    const amountsData: any[] = (data.amounts || []).map((rawData) => {
      if (this.transactionType == ETransactionType.V_SHIPOUT && rawData.total <= 0) {
        return { ...rawData, total: "--" };
      }
      return rawData;
    });
    const amounts: Amount[] = amountsData.map((rawData) => new Amount(rawData));
    this.amounts = amounts;

    const denominations: Denomination[] = [];
    data.denominations?.forEach((rawData) => denominations.push(new Denomination(rawData)));
    this.denominations = denominations;

    const extraDatas: ExtraData[] = [];
    data.extraDatas?.forEach((rawData) => extraDatas.push(new ExtraData(rawData)));
    this.extraDatas = extraDatas;

    const currencies: CurrencyRef[] = [];
    data.currencies?.forEach((rawData) => currencies.push(new CurrencyRef(rawData)));
    this.currencies = currencies;
  }

  private parseDateValue(value: string | number[] | null | undefined): LocalDateTime | undefined {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = value;
      return LocalDateTime.of(year, month, day, hour, minute, second, nano);
    }
    return parseLocalDateTime(value);
  }
}

export class Transactions {
  readonly transactions: Transaction[];
  readonly totalResults: number;
  constructor(data: RawTransactions) {
    this.totalResults = data.totalResult ?? 0;
    const transactions: Transaction[] = [];
    const items = data.transactions ?? [];
    items.forEach((rawData: RawTransaction) => transactions.push(new Transaction(rawData)));
    this.transactions = transactions;
  }
}

export const ETransactionTypeList = Object.fromEntries(Object.entries(ETransactionType).filter(([k, v]) => true));
